import React, { useEffect, useMemo, useState } from "react";
import { Col, Spinner, ListGroup, ListGroupItem } from "reactstrap";

import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import usePersistStoreData from "../../../Components/Hooks/usePersistStoreData";
import { showToast } from "../../../Components/ToastNotification";
import {
  reKyc_EsignRequestThunk,
  reKyc_Esign_DocumentDownloadThunk,
  reKyc_SendRequestThunk,
} from "../../../slices/thunks";
import Message from "../../../Components/Common/Message";

const E_Signature = ({ setSteps, steps }) => {
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const { userData } = usePersistStoreData();
  const token = userData?.token;
  const [isProcessing, setIsProcessing] = useState(false);

  const { reKyc_EsignRequestData, reKyc_EsignRequestLoading } = useSelector(
    (state) => state.Re_Kyc_Esign_Request
  );
  const { reKyc_Esign_DocumentLoading } = useSelector(
    (state) => state.Re_Kyc_Digio_Request_Response
  );

  // for last rekyc request api
  const { reKyc_SendRequestApplicationLoading } = useSelector(
    (state) => state.Re_Kyc_Send_Request_Application
  );

  const proceed_instructions = [
    "Use your Aadhar (mobile OTP) to digitally sign the application form online instantly.",
    "Please allow popup in browser to show e-Sign window.",
  ];

  const download_instructions = [
    "If you don't wish to use quick sign please download and print manually sign and courier the form to us.",
  ];

  // download pdf after e-sign
  const handleDownloadDocument = () => {
    dispatch(
      reKyc_Esign_DocumentDownloadThunk({ params: { type: "kyc" }, token })
    )
      .unwrap()
      .then((result) => {
        const blob = new Blob([result], { type: "application/pdf" });

        const fileURL = window.URL.createObjectURL(blob);

        // Open in new tab
        window.open(fileURL, "_blank");

        // Also download
        const link = document.createElement("a");
        link.href = fileURL;
        link.download = "rekyc.pdf"; // file name
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // showToast("Downloaded successfully", "success");
        navigate("/profile");
        setIsProcessing(false);
      })
      .catch((error) => {
        showToast(
          error?.message || "Something went wrong. Please try again later",
          "error"
        );
      });
  };

  // Open Digio popup with API response data
  const openDigioPopup = (digioData) => {
    if (!window.Digio) {
      // console.error("Digio SDK not loaded");
      showToast(
        "Digio SDK is not loaded. Please refresh the page and try again.",
        "error"
      );
      setIsProcessing(false);
      return;
    }
    try {
      // Digio configuration with callback
      const options = {
        environment: "production", // or "sandbox" for testing
        callback: function (response) {
          if (response.hasOwnProperty("error_code")) {
            // console.log("Error occurred in process:", response);
            // Handle error
            setIsProcessing(false);
            showToast(response?.message, "error");
            return;
          }

          dispatch(reKyc_SendRequestThunk({ token }))
            .then((res) => {
              if (res?.payload?.code === 200) {
                setIsProcessing(false);
                handleDownloadDocument();
                showToast(
                  res?.payload?.message ||
                    "Your re-kyc application has been sent to BN-Secureties ltd.",
                  "success"
                );
              } else {
                setIsProcessing(false);
                showToast(
                  res?.error?.message ||
                    "Something went wrong, please try again",
                  "error"
                );
              }
            })
            .catch((err) => {
              showToast(
                err?.message || "Something went wrong, please try again",
                "error"
              );
            });

          // console.log("Signing completed successfully:", response);
        },
        event_listener: function () {
          // Prevent Digio SDK from throwing when trying to call this
          // console.log("Digio event listener triggered");
          setIsProcessing(false);
        },
      };

      // Create new Digio instance
      const digio = new window.Digio(options);

      // Initialize Digio
      digio.init();

      digio.submit(digioData?.id, digioData?.identifier);
    } catch (error) {
      console.error("Error opening Digio popup:", error);
      showToast("Error opening Digio: " + error.message, "error");
      setIsProcessing(false);
    }
  };

  const handleDigioRequest = () => {
    setIsProcessing(true);
    dispatch(reKyc_EsignRequestThunk({ params: { type: "kyc" }, token }))
      .then((res) => {
        if (res?.payload?.code === 200) {
          openDigioPopup(res?.payload?.data);
          setIsProcessing(false);
        } else {
          showToast(
            res?.error?.message || "Something went wrong, please try again",
            "error"
          );
          setIsProcessing(false);
        }
      })
      .catch((err) => {
        showToast(
          err?.message || "Something went wrong, please try again",
          "error"
        );
        setIsProcessing(false);
      });
  };

  return (
    <React.Fragment>
      <div className="digio_container">
        {userData?.rekcy_appication_status === "rejected" && (
          <div className=" ">
            <Message
              message={userData?.rekcy_appication_sreason}
              color="danger"
              mb={4}
            />
          </div>
        )}
        <ListGroup flush>
          {proceed_instructions?.map((point, index) => (
            <ListGroupItem
              key={index}
              className="d-flex align-items-start fs-14 fw-medium border-0 p-1 pt-0"
            >
              <i className="ri-pushpin-line text-primary me-2 fs-5 flex-shrink-0"></i>

              <span>{point}</span>
            </ListGroupItem>
          ))}
        </ListGroup>
        <div className="my-3">
          <button
            type="button"
            className="btn btn-primary"
            style={{
              minWidth: "120px",
            }}
            disabled={
              reKyc_EsignRequestLoading ||
              isProcessing ||
              reKyc_Esign_DocumentLoading ||
              reKyc_SendRequestApplicationLoading
            }
            onClick={handleDigioRequest}
          >
            {reKyc_EsignRequestLoading ||
            isProcessing ||
            reKyc_SendRequestApplicationLoading ||
            reKyc_Esign_DocumentLoading ? (
              <Spinner size="sm" />
            ) : (
              "Proceed to e-Sign"
            )}
          </button>
        </div>
        {/* <ListGroup flush>
          {download_instructions?.map((point, index) => (
            <ListGroupItem
              key={index}
              className="d-flex align-items-start fs-14 fw-medium border-0 p-1 pt-0"
            >
             <i className="ri-pushpin-line text-primary me-2 fs-5 flex-shrink-0"></i>

              <span>{point}</span>
            </ListGroupItem>
          ))}
        </ListGroup>
        <div className="my-3">
          <button
            type="button"
            className="btn btn-primary"
            
            style={{
              minWidth: "120px",
            }}
          >
            Download Documents
          </button>
        </div> */}
        <Col lg={12}>
          <div className="hstack gap-2 justify-content-end">
            <button
              type="button"
              className="btn btn-soft-secondary"
              onClick={() => {
                setSteps(steps - 1);
              }}
            >
              Prev
            </button>
          </div>
        </Col>
      </div>
    </React.Fragment>
  );
};

export default E_Signature;
